/**
 * Daily Matters — waitlist enhancements.
 *
 * Everything on the waitlist page renders and works without JavaScript
 * (radio-driven tabs, a native <form>, <details> accordions).
 * This module only adds the cross-section niceties:
 *
 *  1. keeps the flavour choice from the sign-up chips in sync with the "first choice" label
 *     in the offer section
 *  2. Klaviyo client API helpers (public key only): subscribe to the list, write a property
 *  3. the post-signup survey: the section's question blocks are asked one at a time and every
 *     answer is stored on the Klaviyo profile
 *  4. the sign-up form: checks the e-mail address more strictly than the browser does, subscribes it
 *     to Klaviyo and only then lets Shopify's captcha post the customer form natively; the success
 *     page Shopify renders after that post picks the sign-up up again (a Klaviyo retry when needed,
 *     then the survey), and when Shopify refuses the post, its error page gets the sign-up back in the
 *     form. A filled honeypot field is reported to the measurement layers, never blocked
 *  5. "copy link" share button
 *  6. reveals the sticky CTA bar once the hero has scrolled out of view and hides it again while
 *     the sign-up form or the closing banner is on screen, or after signing up
 *  7. keeps Horizon's marquee (the ticker) running on touch screens: its hover slow-down only
 *     reacts to a real mouse
 *  8. remembers the last sign-up button clicked (data-dm-cta: "hero", "vip_midden", "offer",
 *     "closing_cta", "sticky_bar"), which the sign-up stores on the Klaviyo profile as
 *     waitlist_entry_point ("direct" when the visitor reached the form without one)
 *  9. tells the measurement layers (dm-clarity.js, dm-meta.js) what only this module knows, as DOM
 *     events on document: dm:submit, dm:subscribed, dm:klaviyo-error, dm:shopify-error,
 *     dm:survey-answer, dm:survey-done
 * 10. remembers the utm_* parameters of the last campaign link for 30 days (localStorage, so a visitor
 *     who returns later without them keeps the ad that brought them), which the sign-up stores on the
 *     Klaviyo profile as waitlist_utm_source, waitlist_utm_medium, waitlist_utm_campaign,
 *     waitlist_utm_content and waitlist_utm_term
 */
(() => {
  /** Dispatches a DOM event for dm-clarity.js. Never put personal data (e-mail, name, free text) in a detail. */
  const signal = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  const storage = {
    get(key) {
      try {
        return sessionStorage.getItem(key) || '';
      } catch (error) {
        return '';
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {
        /* private mode — ignore */
      }
    },
  };

  /**
   * The sign-up between the submit and the success page Shopify renders after its native post: e-mail
   * address, first name, flavour, entry point, whether Klaviyo has it and whether it was announced to the
   * measurement layers. localStorage first, because Meta's in-app browsers on Android can open the page
   * after the post without the sessionStorage of the page before it. It holds personal data, so it expires
   * after an hour and drops the e-mail address and name once the survey is over. dm-meta.js reads it too.
   */
  const SIGNUP_KEY = 'dm-signup';
  const SIGNUP_MAX_AGE_MS = 60 * 60 * 1000;
  const SIGNUP_AREAS = ['localStorage', 'sessionStorage'];

  const signupRecord = {
    read() {
      for (const area of SIGNUP_AREAS) {
        try {
          const record = JSON.parse(window[area].getItem(SIGNUP_KEY) || 'null');
          if (record && Date.now() - record.at < SIGNUP_MAX_AGE_MS) return record;
          if (record) window[area].removeItem(SIGNUP_KEY);
        } catch (error) {
          /* unavailable or unreadable — try the next one */
        }
      }
      return null;
    },
    write(record) {
      const value = JSON.stringify(record);
      SIGNUP_AREAS.forEach((area) => {
        try {
          window[area].setItem(SIGNUP_KEY, value);
        } catch (error) {
          /* private mode — ignore */
        }
      });
    },
    /** Drops the e-mail address and name; the flags stay, so a reload does not count the sign-up twice. */
    forget() {
      const record = signupRecord.read();
      if (!record) return;
      delete record.email;
      delete record.name;
      delete record.firstName;
      signupRecord.write(record);
    },
  };

  /**
   * When this browser last announced a sign-up: a time stamp without personal data, so it outlives the record. Meta's
   * in-app browsers reopen the success page hours or days after the post, when the record has expired, and each
   * reopening counted as a new sign-up: another Lead in Meta, another waitlist_signup in Clarity.
   */
  const ANNOUNCED_KEY = 'dm-signup-announced';
  const ANNOUNCED_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

  const announcedSignup = {
    mark() {
      try {
        localStorage.setItem(ANNOUNCED_KEY, String(Date.now()));
      } catch (error) {
        /* private mode — ignore */
      }
    },
    recent() {
      try {
        const at = Number(localStorage.getItem(ANNOUNCED_KEY));
        return at > 0 && Date.now() - at < ANNOUNCED_MAX_AGE_MS;
      } catch (error) {
        return false;
      }
    },
  };

  /* 10. Campaign parameters, read before anything else can change the URL ---- */
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const UTM_STORAGE_KEY = 'dm-utm';
  const UTM_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

  const campaign = (() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = {};
    UTM_KEYS.forEach((key) => {
      const value = (params.get(key) || '').trim();
      if (value) fromUrl[key] = value.slice(0, 200);
    });
    try {
      if (Object.keys(fromUrl).length) {
        localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify({ at: Date.now(), utm: fromUrl }));
        return fromUrl;
      }
      const saved = JSON.parse(localStorage.getItem(UTM_STORAGE_KEY) || 'null');
      if (saved && saved.utm && Date.now() - saved.at < UTM_MAX_AGE_MS) return saved.utm;
    } catch (error) {
      /* private mode or an unreadable value — ignore */
    }
    return {};
  })();

  /* 1. Flavour sync ------------------------------------------------------- */
  const syncFlavour = (key, name) => {
    document.querySelectorAll('input[data-dm-flavour]').forEach((input) => {
      if (input.dataset.dmFlavour === key && !input.checked) input.checked = true;
    });
    if (name) {
      document.querySelectorAll('[data-dm-flavour-name-target]').forEach((el) => {
        el.textContent = name;
      });
      storage.set('dm-flavour-name', name);
    }
  };

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.dataset.dmFlavour) {
      syncFlavour(input.dataset.dmFlavour, input.dataset.dmFlavourName);
    }
  });

  const initial = document.querySelector('input[data-dm-flavour][data-dm-primary]:checked') ||
    document.querySelector('input[data-dm-flavour]:checked');
  if (initial) syncFlavour(initial.dataset.dmFlavour, initial.dataset.dmFlavourName);

  /* 2. Klaviyo ------------------------------------------------------------ */
  const KLAVIYO_SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions/';
  const KLAVIYO_PROFILE_URL = 'https://a.klaviyo.com/client/profiles/';
  const KLAVIYO_REVISION = '2026-07-15';
  // The visitor waits for the subscription before the page moves on, so do not wait forever. A timed-out
  // subscription is tried again on the success page.
  const KLAVIYO_TIMEOUT_MS = 6000;

  const fieldValue = (form, selector) => {
    const input = form.querySelector(selector);
    return input ? input.value.trim() : '';
  };

  /** POSTs to a Klaviyo client endpoint. Resolves to true on success, false on any failure. */
  const klaviyoRequest = async (url, companyId, payload) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), KLAVIYO_TIMEOUT_MS);
    // Stays 'network' when fetch itself throws: offline, CORS or an ad blocker.
    let status = 'network';
    try {
      const response = await fetch(`${url}?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      status = String(response.status);
      if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
      return true;
    } catch (error) {
      if (error.name === 'AbortError') status = 'timeout';
      console.error('[dm-waitlist] Klaviyo request failed', error);
      signal('dm:klaviyo-error', {
        endpoint: url === KLAVIYO_SUBSCRIBE_URL ? 'subscription' : 'profile',
        status,
      });
      return false;
    } finally {
      window.clearTimeout(timer);
    }
  };

  /**
   * Subscribes the visitor to the list configured on the form, with the flavour, the button that led
   * to the form, the page version (data-page-version on the form) and the campaign parameters as
   * profile properties.
   */
  const subscribeToKlaviyo = (form, { email, firstName, flavour, entryPoint }) => {
    const { klaviyoCompanyId, klaviyoListId, klaviyoSource, pageVersion } = form.dataset;
    if (!klaviyoCompanyId || !klaviyoListId || !email) return Promise.resolve(false);

    const attributes = {
      email,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
    };
    if (firstName) attributes.first_name = firstName;
    const properties = {};
    if (flavour) properties.waitlist_flavour = flavour;
    if (entryPoint) properties.waitlist_entry_point = entryPoint;
    if (pageVersion) properties.waitlist_page_version = pageVersion;
    Object.entries(campaign).forEach(([key, value]) => {
      properties[`waitlist_${key}`] = value;
    });
    if (Object.keys(properties).length) attributes.properties = properties;

    return klaviyoRequest(KLAVIYO_SUBSCRIBE_URL, klaviyoCompanyId, {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: klaviyoSource || 'Waitlist',
          profile: { data: { type: 'profile', attributes } },
        },
        relationships: { list: { data: { type: 'list', id: klaviyoListId } } },
      },
    });
  };

  /** Writes survey answers to the profile. Multiple answers are stored as a list property. */
  const writeKlaviyoProperties = (form, email, properties) => {
    const { klaviyoCompanyId } = form.dataset;
    if (!klaviyoCompanyId || !email) return Promise.resolve(false);
    return klaviyoRequest(KLAVIYO_PROFILE_URL, klaviyoCompanyId, {
      data: { type: 'profile', attributes: { email, properties } },
    });
  };

  /* 3. Post-signup survey ------------------------------------------------- */
  // The survey is the last use of the e-mail address in this browser, but dm-meta.js still needs it when the
  // sign-up is announced, so the record forgets it only once both have happened.
  let surveyOver = false;
  const forgetWhenDone = () => {
    const record = signupRecord.read();
    if (surveyOver && (!record || record.announced)) signupRecord.forget();
  };

  const finishSurvey = (form, answered) => {
    const survey = form.querySelector('[data-dm-survey]');
    const done = form.querySelector('[data-dm-survey-done]');
    const thanks = form.querySelector('[data-dm-survey-thanks]');
    if (survey) survey.hidden = true;
    if (thanks) thanks.hidden = !answered;
    if (done) done.hidden = false;
    surveyOver = true;
    forgetWhenDone();
  };

  /** Asks the question blocks one at a time; every answer is saved to the Klaviyo profile. */
  const startSurvey = (form, email) => {
    const survey = form.querySelector('[data-dm-survey]');
    const steps = survey ? Array.from(survey.querySelectorAll('[data-dm-survey-step]')) : [];
    if (!survey || !steps.length || !email) {
      finishSurvey(form, false);
      return;
    }
    if (survey.dataset.started) return;
    survey.dataset.started = 'true';

    const progress = survey.querySelector('[data-dm-survey-progress]');
    let answered = 0;

    const show = (index) => {
      steps.forEach((step, i) => {
        step.hidden = i !== index;
      });
      if (index >= steps.length) {
        signal('dm:survey-done', { answered, total: steps.length });
        finishSurvey(form, answered > 0);
        return;
      }
      if (progress && progress.dataset.template) {
        progress.textContent = progress.dataset.template
          .replace('[n]', String(index + 1))
          .replace('[total]', String(steps.length));
      }
      const legend = steps[index].querySelector('legend');
      if (legend) {
        legend.tabIndex = -1;
        legend.focus({ preventScroll: true });
      }
    };

    steps.forEach((step, index) => {
      const property = step.dataset.property;
      const multi = step.dataset.mode === 'multi';
      const max = parseInt(step.dataset.max || '0', 10);
      const options = Array.from(step.querySelectorAll('[data-dm-survey-option]'));
      const checks = Array.from(step.querySelectorAll('[data-dm-survey-check]'));
      const next = step.querySelector('[data-dm-survey-next]');
      const skip = step.querySelector('[data-dm-survey-skip]');
      const otherField = step.querySelector('[data-dm-survey-other-field]');
      const otherInput = step.querySelector('[data-dm-survey-other-input]');

      const isOther = (el) => el.hasAttribute('data-dm-survey-other');
      const otherText = () => (otherInput ? otherInput.value.trim().slice(0, 200) : '');
      const toggleOther = (on) => {
        if (!otherField) return;
        otherField.hidden = !on;
        if (on && otherInput) otherInput.focus({ preventScroll: true });
      };

      // value: the chosen label(s); other: free text typed for the "other" option.
      const advance = (value, other, delay) => {
        const properties = {};
        if (property && value && value.length) properties[property] = value;
        if (property && other) properties[`${property}_other`] = other;
        const skipped = !Object.keys(properties).length;
        if (!skipped) {
          answered += 1;
          writeKlaviyoProperties(form, email, properties);
        }
        signal('dm:survey-answer', { property, skipped, step: index + 1, total: steps.length });
        window.setTimeout(() => show(index + 1), delay || 0);
      };

      options.forEach((option) => {
        option.addEventListener('click', () => {
          options.forEach((other) => other.setAttribute('aria-pressed', String(other === option)));
          if (isOther(option)) {
            toggleOther(true);
            if (next) {
              next.hidden = false;
              next.disabled = false;
            }
            return;
          }
          toggleOther(false);
          if (next) next.hidden = true;
          advance(option.value, '', 180);
        });
      });

      checks.forEach((check) => {
        check.addEventListener('change', () => {
          const chosen = checks.filter((c) => c.checked).length;
          if (max > 0) {
            checks.forEach((c) => {
              c.disabled = !c.checked && chosen >= max;
            });
          }
          if (next) next.disabled = chosen === 0;
          if (isOther(check)) toggleOther(check.checked);
        });
      });

      if (next) {
        next.addEventListener('click', () => {
          if (multi) {
            const chosen = checks.filter((c) => c.checked);
            advance(chosen.map((c) => c.value), chosen.some(isOther) ? otherText() : '');
          } else {
            const pressed = options.find((o) => o.getAttribute('aria-pressed') === 'true');
            advance(pressed ? pressed.value : '', pressed && isOther(pressed) ? otherText() : '');
          }
        });
      }
      if (otherInput && next) {
        otherInput.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          if (!next.disabled && !next.hidden) next.click();
        });
      }
      if (skip) skip.addEventListener('click', () => advance(null, ''));
    });

    survey.hidden = false;
    show(0);
  };

  // Theme editor: selecting a question block reveals the success state with that question.
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const step = target ? target.closest('[data-dm-survey-step]') : null;
    const form = step ? step.closest('form[data-dm-signup-form]') : null;
    if (!step || !form) return;
    const fields = form.querySelector('[data-dm-signup-fields]');
    const box = form.querySelector('[data-dm-signup-success]');
    const survey = form.querySelector('[data-dm-survey]');
    if (fields) fields.hidden = true;
    if (box) box.hidden = false;
    if (survey) survey.hidden = false;
    form.querySelectorAll('[data-dm-survey-step]').forEach((other) => {
      other.hidden = other !== step;
    });
  });

  /* 4. Sign-up form ------------------------------------------------------- */
  // First Klaviyo, then Shopify. Shopify guards every customer form with a captcha: an inline bootstrap
  // script plus an hCaptcha provider that binds to the form on its first focus or change. From then on it
  // answers every submit with a native post as soon as its token is ready, whatever other listeners did with
  // preventDefault(), so the page used to leave while the Klaviyo request was still under way. Hence the
  // listener below runs before Shopify's (capture phase on window), holds the submit until Klaviyo has
  // answered and only then hands it to the captcha, whose native post creates the Shopify customer (tags,
  // marketing consent) and reloads the page in its success state. Posting the customer form in the
  // background is no alternative: Shopify answers that with "Missing CAPTCHA token".
  const HANDOFF_TIMEOUT_MS = 15000;

  const signupData = (form) => {
    const flavourInput = form.querySelector('input[data-dm-flavour]:checked');
    const name = fieldValue(form, 'input[name="contact[first_name]"]');
    return {
      email: fieldValue(form, 'input[name="contact[email]"]'),
      // As typed, to put back if Shopify refuses the post; Klaviyo and the success page use the first word.
      name,
      firstName: name.split(' ')[0],
      flavour: flavourInput ? flavourInput.dataset.dmFlavourName : '',
      entryPoint: storage.get('dm-cta') || 'direct',
    };
  };

  const personalizeSuccess = (box, { firstName, flavour }) => {
    const title = box.querySelector('[data-dm-thanks-title]');
    if (title && firstName && title.dataset.named) {
      title.textContent = title.dataset.named.replace('[name]', firstName);
    }
    if (flavour) {
      box.querySelectorAll('[data-dm-flavour-name-target]').forEach((el) => {
        el.textContent = flavour;
      });
    }
  };

  /** Swaps the card to the success state in place, for when Shopify's post does not come (see handOff). */
  const showSuccess = (form, data) => {
    const fields = form.querySelector('[data-dm-signup-fields]');
    const box = form.querySelector('[data-dm-signup-success]');
    if (fields) fields.hidden = true;
    if (!box) return;
    form.dataset.completed = 'true';
    personalizeSuccess(box, data);
    box.hidden = false;
    document.querySelectorAll('[data-dm-sticky-bar]').forEach((bar) => {
      bar.hidden = true;
    });
    if (typeof box.focus === 'function') box.focus({ preventScroll: true });
    startSurvey(form, data.email);
  };

  /**
   * Tells the measurement layers about a sign-up (dm-meta.js turns it into the Meta Lead), once per sign-up
   * however often its success state renders. via: "klaviyo" when Klaviyo took it before Shopify's post,
   * "retry" when only the success page got it into Klaviyo, "shopify" when only Shopify has it.
   */
  const announce = (record, via, { entryPoint, flavour }) => {
    if (record) {
      if (record.announced) return;
      record.announced = true;
      signupRecord.write(record);
    }
    announcedSignup.mark();
    signal('dm:subscribed', { via, entryPoint, flavour });
    forgetWhenDone();
  };

  const setBusy = (form, busy) => {
    const button = form.querySelector('[data-dm-signup-fields] [type="submit"]');
    if (!button) return;
    button.disabled = busy;
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  };

  /** The form's message for a sign-up that neither Klaviyo nor Shopify took. */
  const toggleFailure = (form, show) => {
    const message = form.querySelector('[data-dm-signup-failed]');
    if (message) message.hidden = !show;
  };

  // Shopify saves a cookie-banner choice with a request of its own and only sets the cookie once the answer is
  // in (then it fires visitorConsentCollected). A choice made just before the form left the page was cut off
  // with it: the banner asked again on the success page, where a refusal also lost the Meta Lead. So the page
  // only leaves once such a save is done, or after CONSENT_WAIT_MS.
  const CONSENT_WAIT_MS = 3000;
  const CONSENT_CHOICES = [
    '#shopify-pc__banner__btn-accept',
    '#shopify-pc__banner__btn-decline',
    '#shopify-pc__prefs__header-accept',
    '#shopify-pc__prefs__header-decline',
    '#shopify-pc__prefs__header-save',
  ].join(',');
  let consentSaved = Promise.resolve();
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element) || !event.target.closest(CONSENT_CHOICES)) return;
      consentSaved = new Promise((resolve) => {
        document.addEventListener('visitorConsentCollected', () => resolve(), { once: true });
        window.setTimeout(resolve, CONSENT_WAIT_MS);
      });
    },
    true
  );

  // The captcha posts the form a moment after the hand-off, once its token is in. It takes the form's submit()
  // as it finds it when it binds (on the first focus in the form), so a submit() set before that lets this
  // last step wait for a cookie choice too. Never replace one that is already there: that is the captcha's own.
  document.querySelectorAll('form[data-dm-signup-form]').forEach((form) => {
    if (form.hasAttribute('data-hcaptcha-bound') || Object.prototype.hasOwnProperty.call(form, 'submit')) return;
    form.submit = () => {
      consentSaved.then(() => HTMLFormElement.prototype.submit.call(form));
    };
  });

  /**
   * Lets the held submit through to Shopify's captcha. Calls onStall when the page has not started to leave
   * HANDOFF_TIMEOUT_MS later: captcha blocked or failing, or a captcha challenge still open.
   */
  const handOff = (form, onStall) => {
    let leaving = false;
    window.addEventListener(
      'pagehide',
      () => {
        leaving = true;
      },
      { once: true }
    );
    window.setTimeout(() => {
      if (!leaving) onStall();
    }, HANDOFF_TIMEOUT_MS);

    form.dataset.dmHandoff = 'true';
    const submit = () => {
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    };
    // The captcha binds on a focus or change in the form. A form filled without either (some autofill) is
    // still unbound, and the bootstrap silently swallows the submit of an unbound form: protect() binds it.
    const captcha = window.Shopify && window.Shopify.captcha;
    if (captcha && typeof captcha.protect === 'function' && !form.hasAttribute('data-hcaptcha-bound')) {
      captcha.protect(form, submit);
    } else {
      submit();
    }
  };

  // The browser's type="email" takes "jan@gmail" and "jan@gmail..com". Such an address never gets mail, Klaviyo
  // may refuse it, and when Shopify refuses it the visitor lands on an error page (on 10 October 2026 an iPhone
  // visitor gave up there). So the form asks for a domain with a dot and a real ending before anything leaves.
  const plausibleEmail = (value) => {
    const at = value.indexOf('@');
    if (at < 1 || at !== value.lastIndexOf('@') || /\s/.test(value)) return false;
    const local = value.slice(0, at);
    if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
    const labels = value.slice(at + 1).split('.');
    if (labels.length < 2 || labels.some((label) => !label || label.startsWith('-') || label.endsWith('-'))) {
      return false;
    }
    const ending = labels[labels.length - 1];
    return ending.length >= 2 && !/^\d+$/.test(ending);
  };

  /** Makes the browser's own validation stop the submit, with dm.signup.email_invalid, while the address is off. */
  const checkEmail = (input) => {
    const value = input.value.trim();
    input.setCustomValidity(!value || plausibleEmail(value) ? '' : input.dataset.dmEmailInvalid || '');
  };

  const signupEmail = (element) =>
    element instanceof HTMLInputElement && element.name === 'contact[email]' && element.closest('[data-dm-signup-form]')
      ? element
      : null;

  document.addEventListener('input', (event) => {
    const input = signupEmail(event.target);
    if (input) checkEmail(input);
  });
  // Autofill does not always fire input, so check again on the click that starts the browser's validation.
  // Enter in a field clicks the submit button as well.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target ? target.closest('[data-dm-signup-form] [type="submit"]') : null;
      const input = button && button.form && signupEmail(button.form.querySelector('input[name="contact[email]"]'));
      if (input) checkEmail(input);
    },
    true
  );

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-dm-signup-form]')) return;
      // The hand-off below: from here on the submit belongs to Shopify's captcha.
      if (form.dataset.dmHandoff) return;
      event.preventDefault();
      // Shopify's captcha listeners come after this one and would post the form right away.
      event.stopImmediatePropagation();
      if (form.dataset.completed || form.dataset.dmBusy) return;
      // A submit that skipped the click above (a script calling requestSubmit, say) is checked here.
      const email = signupEmail(form.querySelector('input[name="contact[email]"]'));
      if (email) {
        checkEmail(email);
        // reportValidity() fires the one `invalid` event dm-clarity.js counts; checkValidity() would add a second.
        if (!email.validity.valid) {
          email.reportValidity();
          return;
        }
      }
      // A filled honeypot is reported, never blocked: autofill in the Facebook and Instagram in-app browsers on
      // Android filled it for real visitors, whose sign-ups vanished without a word.
      const honeypot = form.querySelector('[data-dm-honeypot]');
      signal('dm:submit', { honeypot: Boolean(honeypot && honeypot.value) });

      const data = signupData(form);
      const record = { ...data, at: Date.now(), klaviyo: false, announced: false };
      signupRecord.write(record);
      form.dataset.dmBusy = 'true';
      setBusy(form, true);
      toggleFailure(form, false);

      // Without a list ID Klaviyo is switched off and Shopify alone takes the sign-up.
      const klaviyo = form.dataset.klaviyoListId ? subscribeToKlaviyo(form, data) : Promise.resolve(false);
      klaviyo
        .then((ok) => {
          record.klaviyo = ok;
          signupRecord.write(record);
          // Read only now: a cookie choice made while Klaviyo answered counts as well.
          return consentSaved.then(() => ok);
        })
        .then((ok) => {
          handOff(form, () => {
            delete form.dataset.dmHandoff;
            delete form.dataset.dmBusy;
            setBusy(form, false);
            if (ok) {
              // Klaviyo has the sign-up, only the Shopify copy is missing: finish here.
              announce(record, 'klaviyo', data);
              showSuccess(form, data);
            } else {
              toggleFailure(form, true);
            }
          });
        });
    },
    true
  );

  // The success state Shopify renders after its native post. The sign-up kept at submit still has to reach
  // Klaviyo when that failed before the post, and its e-mail address carries the survey.
  document.querySelectorAll('[data-dm-signup-success]:not([hidden])').forEach((box) => {
    const form = box.closest('form[data-dm-signup-form]');
    // No record: the record expired and the page was opened again, JavaScript was off at submit, or the browser keeps
    // no storage. Shopify has the customer.
    const record = signupRecord.read();
    const data = record || {
      email: '',
      firstName: '',
      flavour: storage.get('dm-flavour-name'),
      entryPoint: storage.get('dm-cta') || 'direct',
    };
    personalizeSuccess(box, data);
    document.querySelectorAll('[data-dm-sticky-bar]').forEach((bar) => {
      bar.hidden = true;
    });
    if (typeof box.focus === 'function') box.focus({ preventScroll: true });
    if (!form) return;
    form.dataset.completed = 'true';
    if (record && record.klaviyo) {
      // Only after DOMContentLoaded do dm-clarity.js and dm-meta.js listen for sure, whichever module runs first.
      document.addEventListener('DOMContentLoaded', () => announce(record, 'klaviyo', data), { once: true });
      startSurvey(form, data.email);
      return;
    }
    // The success page opened again after the record expired, for a sign-up this browser already announced.
    if (!record && announcedSignup.recent()) {
      finishSurvey(form, false);
      return;
    }
    subscribeToKlaviyo(form, data).then((ok) => {
      if (record) {
        record.klaviyo = ok;
        signupRecord.write(record);
      }
      announce(record, ok ? 'retry' : 'shopify', data);
      if (ok) startSurvey(form, data.email);
      else finishSurvey(form, false);
    });
  });

  // Shopify refused its post (an address it does not take, a failed captcha) and sent the form back with an error,
  // but without the e-mail address and the name; it checks what is left again, so the error usually says the e-mail
  // is empty. Put back what this browser sent, so the visitor only has to correct it. When Klaviyo took the sign-up all the same, the visitor is on the VIP list: finish as the
  // success page would instead of asking again.
  const REJECTED_MAX_AGE_MS = 5 * 60 * 1000;

  const refill = (form, { name, firstName, email, flavour }) => {
    const fill = (selector, value) => {
      const input = form.querySelector(selector);
      if (input && value && !input.value) input.value = value;
      return input;
    };
    fill('input[name="contact[first_name]"]', name || firstName);
    const emailInput = signupEmail(fill('input[name="contact[email]"]', email));
    if (emailInput) checkEmail(emailInput);
    const chip = Array.from(form.querySelectorAll('input[data-dm-flavour]')).find(
      (input) => input.dataset.dmFlavourName === flavour
    );
    if (chip) {
      chip.checked = true;
      syncFlavour(chip.dataset.dmFlavour, flavour);
    }
  };

  document.querySelectorAll('[data-dm-signup-rejected]').forEach((error) => {
    const form = error.closest('form[data-dm-signup-form]');
    if (!form) return;
    // Every submit writes a new record, so a fresh one is the sign-up Shopify just refused.
    const saved = signupRecord.read();
    const record = saved && Date.now() - saved.at < REJECTED_MAX_AGE_MS ? saved : null;
    document.addEventListener('DOMContentLoaded', () => signal('dm:shopify-error'), { once: true });
    if (!record) return;
    if (record.klaviyo) {
      document.addEventListener('DOMContentLoaded', () => announce(record, 'klaviyo', record), { once: true });
      showSuccess(form, record);
      return;
    }
    refill(form, record);
  });

  /* 5. Copy link ---------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-dm-copy]') : null;
    if (!button) return;
    const label = button.querySelector('[data-dm-copy-label]');
    const done = () => {
      if (!label) return;
      const original = label.textContent;
      label.textContent = button.dataset.dmCopied || original;
      window.setTimeout(() => {
        label.textContent = original;
      }, 2200);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(button.dataset.dmCopy).then(done, done);
    } else {
      done();
    }
  });

  /* 6. Sticky bar --------------------------------------------------------- */
  // Hidden until the reveal element (the hero) has scrolled out above the viewport, and again
  // while any watched element (the sign-up form, the closing banner) is on screen. The section renders the bar hidden when a reveal
  // element is configured, so this only ever removes the attribute on first show.
  document.querySelectorAll('[data-dm-sticky-bar]').forEach((bar) => {
    const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
    if (posted) {
      bar.hidden = true;
      return;
    }
    const query = (selector) => (selector ? document.querySelector(selector) : null);
    const reveal = query(bar.dataset.dmRevealAfter);
    const watched = bar.dataset.dmWatch ? [...document.querySelectorAll(bar.dataset.dmWatch)] : [];
    const state = { beforeReveal: Boolean(reveal), watching: new Set(), pinned: false };
    const apply = () => {
      bar.toggleAttribute('data-dm-hidden', !state.pinned && (state.beforeReveal || state.watching.size > 0));
    };

    if (!('IntersectionObserver' in window)) {
      state.beforeReveal = false;
      apply();
      return;
    }
    if (reveal) {
      new IntersectionObserver(([entry]) => {
        // Scrolled past = the element sits entirely above the viewport.
        state.beforeReveal = entry.boundingClientRect.bottom > 0;
        apply();
      }).observe(reveal);
    }
    if (watched.length) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) state.watching.add(entry.target);
            else state.watching.delete(entry.target);
          });
          apply();
        },
        { threshold: 0.12 }
      );
      watched.forEach((element) => observer.observe(element));
    }
    apply();

    // Theme editor: keep the bar on screen while its section is selected, whatever the scroll position.
    const section = bar.closest('.shopify-section');
    if (section && window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        state.pinned = event.target === section;
        apply();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target === section) state.pinned = false;
        apply();
      });
    }
  });

  /* 7. Ticker ------------------------------------------------------------- */
  // Horizon's marquee eases to a standstill on pointerenter (a hover effect meant for the mouse)
  // and only eases back on pointerleave. A finger fires pointerenter as well, but a touch does not
  // reliably "leave" the way a mouse does (a scroll cancels the pointer, and some browsers follow a
  // tap with emulated mouse events that only leave on the next tap elsewhere), so the ticker can
  // stay frozen. Capture the event first and only let a real mouse on a device that can hover
  // through to the theme's listener; the one-second window catches mouse events emulated from a
  // touch on hybrid devices.
  const noHover = window.matchMedia('(hover: none)');
  const guardTicker = (marquee) => {
    if (marquee.hasAttribute('data-dm-ticker-guard')) return;
    marquee.setAttribute('data-dm-ticker-guard', '');
    let lastTouch = 0;
    const touched = (event) => {
      if (event.pointerType !== 'mouse') lastTouch = Date.now();
    };
    ['pointerdown', 'pointerup', 'pointercancel', 'pointerleave'].forEach((type) => {
      marquee.addEventListener(type, touched, true);
    });
    marquee.addEventListener(
      'pointerenter',
      (event) => {
        touched(event);
        if (event.pointerType !== 'mouse' || noHover.matches || Date.now() - lastTouch < 1000) {
          event.stopImmediatePropagation();
        }
      },
      true
    );
  };
  document.querySelectorAll('marquee-component').forEach(guardTicker);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('marquee-component').forEach(guardTicker);
  });

  /* 8. Entry point ------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const cta = event.target instanceof Element ? event.target.closest('[data-dm-cta]') : null;
    if (cta && cta.dataset.dmCta) storage.set('dm-cta', cta.dataset.dmCta);
  });
})();
